// 🎙 회의록 공유 대상 고르기 — 시작 창·회의록 창 공용.
//   2026-10-27 형 "대화방 검색하듯이 초성이나 이름 넣으면 자동으로 추가 / 부서로 하든 특정 대화방 전체 멤버를 넣든 /
//   선택된 목록은 부서-이름 / 사람이 많아질 걸 대비해 편하게 볼 수 있게":
//   · 검색 = 조직도(사용자용 트리 — 표시 제외 부서는 이미 빠져 온다) 사람·부서 + 내 대화방, 초성 매칭(matchesAny).
//   · 부서를 고르면 하위 부서까지 전원, 대화방을 고르면 그 방 멤버 전원(퇴사자 제외, member-names).
//   · 선택 목록은 부서별로 묶고 줄마다 "부서 - 이름", 개수·부서째 빼기·모두 지우기, 높이 제한 스크롤.
//   · readOnly = 회의록 창의 공유 대상 보기(같은 묶음 표시, 편집 버튼 없음). value 에 이름이 없어도(username 만) 조직도로 채워 보인다.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrgDept } from '@tpt/design';
import { useAuthStore } from '../../stores/authStore';
import { loadOrgTree } from '../../api/orgTree';
import { rcSubscriptions, type RcChannel } from '../../api/rcClient';
import { roomTitleOf } from '../projects/pjRoomNames';
import { matchesAny } from '../../utils/hangul';
import { Icon } from '../icons';

export interface SharePerson { username: string; name: string; dept?: string }

const USER_RE = /^[A-Za-z0-9._@-]{2,80}$/;   // 서버 cleanShares 와 같은 형태만
type DeptHit = { id: string; name: string; ctx: string; members: string[] };
type Hit = { kind: 'user'; p: SharePerson } | { kind: 'dept'; d: DeptHit } | { kind: 'room'; r: RcChannel; title: string };

/** 조직도 → 사람(username → 이름·대표 부서) + 부서(하위 포함 인원) */
function indexOrg(root: OrgDept): { people: Map<string, SharePerson>; depts: DeptHit[] } {
	const people = new Map<string, SharePerson & { side?: boolean }>();
	const depts: DeptHit[] = [];
	const walk = (n: OrgDept, ctx: string[]): string[] => {
		const isCompany = !n.id || n.id.startsWith('company:');
		const mine: string[] = [];
		for (const u of n.users || []) {
			const un = u.rcUsername;
			if (!un || !USER_RE.test(un)) continue;
			mine.push(un);
			const side = !!u.jobDept;   // 겸직 배치보다 기본부서 배치를 대표 부서로
			const prev = people.get(un);
			if (!prev || (prev.side && !side)) people.set(un, { username: un, name: u.name || un, dept: u.deptName || n.name, side });
		}
		const nextCtx = n.id ? [...ctx, n.name] : ctx;
		for (const c of n.children || []) mine.push(...walk(c, nextCtx));
		const members = [...new Set(mine)];
		if (n.id && !n.id.startsWith('gw-') && members.length) depts.push({ id: n.id, name: n.name, ctx: (isCompany ? [] : ctx.slice(-2)).join(' · '), members });
		return members;
	};
	walk(root, []);
	return { people, depts };
}

export default function SharePicker({ value, onChange, testId = 'meetrec-share', readOnly = false }: { value: SharePerson[]; onChange?: (v: SharePerson[]) => void; testId?: string; readOnly?: boolean }) {
	const { t } = useTranslation();
	const authToken = useAuthStore((s) => s.authToken);
	const userId = useAuthStore((s) => s.userId);
	const me = useAuthStore((s) => s.username);
	const [org, setOrg] = useState<{ people: Map<string, SharePerson>; depts: DeptHit[] } | null>(null);
	const [rooms, setRooms] = useState<RcChannel[]>([]);
	const [q, setQ] = useState('');
	const [active, setActive] = useState(0);
	const [busy, setBusy] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		let alive = true;
		void loadOrgTree().then((tr) => { if (alive) setOrg(indexOrg(tr)); }).catch(() => { if (alive) setOrg({ people: new Map(), depts: [] }); });
		return () => { alive = false; };
	}, []);
	useEffect(() => {
		if (readOnly || !authToken || !userId) return;
		let alive = true;
		void rcSubscriptions(authToken, userId).then((l) => { if (alive) setRooms(l.filter((r) => r.t === 'c' || r.t === 'p')); }).catch(() => undefined);
		return () => { alive = false; };
	}, [readOnly, authToken, userId]);

	const info = (p: SharePerson): SharePerson => {
		const o = org?.people.get(p.username);
		return { username: p.username, name: p.name && p.name !== p.username ? p.name : (o?.name || p.name || p.username), dept: p.dept || o?.dept || '' };
	};
	const chosen = useMemo(() => new Set(value.map((v) => v.username)), [value]);

	const hits = useMemo<Hit[]>(() => {
		const s = q.trim();
		if (!s || !org) return [];
		const out: Hit[] = [];
		let n = 0;
		for (const p of org.people.values()) {
			if (p.username === me || chosen.has(p.username)) continue;
			if (!matchesAny([p.name, p.username, p.dept], s)) continue;
			out.push({ kind: 'user', p });
			if (++n >= 8) break;
		}
		n = 0;
		for (const d of org.depts) {
			if (!matchesAny([d.name], s)) continue;
			out.push({ kind: 'dept', d });
			if (++n >= 5) break;
		}
		n = 0;
		for (const r of rooms) {
			const title = roomTitleOf(r);
			if (!matchesAny([title, r.name], s)) continue;
			out.push({ kind: 'room', r, title });
			if (++n >= 5) break;
		}
		return out;
	}, [q, org, rooms, me, chosen]);
	useEffect(() => { setActive(0); }, [q]);

	const addMany = (list: SharePerson[]) => {
		const next = [...value];
		const seen = new Set(chosen);
		for (const p of list) {
			if (!p.username || p.username === me || seen.has(p.username) || !USER_RE.test(p.username)) continue;
			seen.add(p.username);
			next.push(p);
		}
		onChange?.(next);
	};
	const pick = async (h: Hit) => {
		setQ('');
		inputRef.current?.focus();
		if (h.kind === 'user') { addMany([h.p]); return; }
		if (h.kind === 'dept') { addMany(h.d.members.map((u) => org?.people.get(u) || { username: u, name: u })); return; }
		if (!authToken || !userId) return;
		setBusy(true);
		try {
			const res = await fetch(`/api/tpt/room/member-names?roomId=${encodeURIComponent(h.r._id)}`, { headers: { 'X-Auth-Token': authToken, 'X-User-Id': userId } });
			const j = await res.json() as { names?: Record<string, string>; inactive?: string[] };
			const gone = new Set(j.inactive || []);
			addMany(Object.entries(j.names || {}).filter(([u]) => !gone.has(u)).map(([u, nm]) => org?.people.get(u) || { username: u, name: nm || u }));
		} catch { /* 방 멤버를 못 받으면 아무것도 안 넣는다 */ } finally { setBusy(false); }
	};
	const onKey = (e: KeyboardEvent) => {
		if (e.nativeEvent.isComposing) return;   // 한글 조합 중 Enter 무시
		if (e.key === 'ArrowDown' && hits.length) { e.preventDefault(); setActive((a) => (a + 1) % hits.length); }
		else if (e.key === 'ArrowUp' && hits.length) { e.preventDefault(); setActive((a) => (a - 1 + hits.length) % hits.length); }
		else if (e.key === 'Enter' && hits[active]) { e.preventDefault(); void pick(hits[active]); }
		else if (e.key === 'Escape' && q) { e.preventDefault(); e.stopPropagation(); setQ(''); }
	};

	// 선택 목록 — 부서별 묶음(부서 이름순, 안에선 이름순)
	const noDept = t('meetRec.shareNoDept', { defaultValue: '부서 없음' });
	const groups = useMemo(() => {
		const m = new Map<string, SharePerson[]>();
		for (const v of value) {
			const p = info(v);
			const k = p.dept || noDept;
			if (!m.has(k)) m.set(k, []);
			m.get(k)!.push(p);
		}
		return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'ko')).map(([dept, list]) => ({ dept, list: list.sort((a, b) => a.name.localeCompare(b.name, 'ko')) }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, org, noDept]);

	const section = (kind: Hit['kind']) => ({ user: t('meetRec.sharePeople', { defaultValue: '사람' }), dept: t('meetRec.shareDepts', { defaultValue: '부서 (하위 부서 포함 전원)' }), room: t('meetRec.shareRooms', { defaultValue: '대화방 (멤버 전원)' }) })[kind];

	return (
		<div data-testid={testId}>
			{!readOnly && (
				<div className="relative">
					<input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
						placeholder={busy ? t('meetRec.shareLoading', { defaultValue: '불러오는 중…' }) : t('meetRec.sharePh', { defaultValue: '이름·초성·부서·대화방으로 찾아 추가' })}
						className="tpt-input w-full px-3 py-1.5 text-ui-sm" data-testid={`${testId}-input`} disabled={busy} />
					{hits.length > 0 && (
						<div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-72 overflow-y-auto rounded-md border border-line bg-surface py-1 shadow-lg" data-testid={`${testId}-hits`}>
							{hits.map((h, i) => {
								const head = i === 0 || hits[i - 1].kind !== h.kind;
								const cls = `flex w-full items-center gap-2 px-3 py-1.5 text-left text-ui-sm ${i === active ? 'bg-class-blue/10' : 'hover:bg-hover-subtle'}`;
								const key = h.kind === 'user' ? `u:${h.p.username}` : h.kind === 'dept' ? `d:${h.d.id}` : `r:${h.r._id}`;
								return (
									<div key={key}>
										{head && <div className="px-3 pb-0.5 pt-1.5 text-[11px] font-semibold text-fg-subtle">{section(h.kind)}</div>}
										<button type="button" className={cls} onMouseEnter={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); void pick(h); }} data-testid={`${testId}-hit-${h.kind}`}>
											{h.kind === 'user' && (<><span className="truncate">{h.p.dept ? `${h.p.dept} - ` : ''}<b className="font-semibold">{h.p.name}</b></span></>)}
											{h.kind === 'dept' && (<><Icon name="users" size={14} className="shrink-0 text-fg-subtle" /><span className="truncate"><b className="font-semibold">{h.d.name}</b>{h.d.ctx ? <span className="text-ui-xs text-fg-subtle"> ({h.d.ctx})</span> : null}</span><span className="ml-auto shrink-0 text-ui-xs text-class-blue">{t('meetRec.shareAddN', { defaultValue: '+{{n}}명', n: h.d.members.filter((u) => u !== me && !chosen.has(u)).length })}</span></>)}
											{h.kind === 'room' && (<><Icon name="message-circle" size={14} className="shrink-0 text-fg-subtle" /><span className="truncate"><b className="font-semibold">{h.title}</b></span>{h.r.usersCount ? <span className="ml-auto shrink-0 text-ui-xs text-class-blue">{t('meetRec.shareAddN', { defaultValue: '+{{n}}명', n: h.r.usersCount })}</span> : null}</>)}
										</button>
									</div>
								);
							})}
						</div>
					)}
				</div>
			)}
			{value.length > 0 && (
				<div className={`${readOnly ? '' : 'mt-2 '}rounded-lg border border-line`} data-testid={`${testId}-list`}>
					<div className="flex items-center justify-between border-b border-line-subtle bg-surface-subtle px-2.5 py-1 text-ui-xs">
						<span className="font-semibold text-fg">{t('meetRec.shareCount', { defaultValue: '{{n}}명 · {{g}}개 부서', n: value.length, g: groups.length })}</span>
						{!readOnly && <button type="button" onClick={() => onChange?.([])} className="text-fg-subtle hover:text-red-600" data-testid={`${testId}-clear`}>{t('meetRec.shareClear', { defaultValue: '모두 지우기' })}</button>}
					</div>
					<div className="max-h-48 overflow-y-auto py-0.5">
						{groups.map((g) => (
							<div key={g.dept}>
								{groups.length > 1 && (
									<div className="sticky top-0 flex items-center gap-1 bg-surface/95 px-2.5 pt-1 text-[11px] font-semibold text-fg-subtle">
										<span className="truncate">{g.dept} · {g.list.length}</span>
										{!readOnly && <button type="button" aria-label="remove group" title={t('meetRec.shareRemoveGroup', { defaultValue: '이 부서 빼기' })} onClick={() => { const drop = new Set(g.list.map((p) => p.username)); onChange?.(value.filter((v) => !drop.has(v.username))); }} className="ml-auto opacity-60 hover:text-red-600 hover:opacity-100"><Icon name="x" size={12} /></button>}
									</div>
								)}
								{g.list.map((p) => (
									<div key={p.username} className="group flex items-center gap-1 px-2.5 py-0.5 text-ui-xs text-fg" data-testid={`${testId}-item`}>
										<span className="truncate">{p.dept ? `${p.dept} - ` : ''}{p.name}</span>
										{!readOnly && <button type="button" aria-label="remove" onClick={() => onChange?.(value.filter((v) => v.username !== p.username))} className="ml-auto opacity-40 hover:text-red-600 hover:opacity-100"><Icon name="x" size={12} /></button>}
									</div>
								))}
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
